// src/utils/tokenManager.ts

const API_BASE: string = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface SessionResponse {
    token: string;
}

class TokenManager {
    public token: string | null;
    private isInitializing: boolean;

    constructor() {
        this.token = typeof window !== 'undefined' ? sessionStorage.getItem('ff_security_token') : null;
        this.isInitializing = false;
    }

    async getToken(): Promise<string | null> {
        if (this.token) return this.token;

        if (typeof window === 'undefined') return null;

        // Check session storage again
        this.token = sessionStorage.getItem('ff_security_token');
        if (this.token) return this.token;

        if (this.isInitializing) {
            while (this.isInitializing) {
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
            return this.token;
        }

        this.isInitializing = true;
        try {
            // Add a timestamp to prevent CDN caching
            const cacheBuster = `?t=${Date.now()}`;
            const response = await fetch(`${API_BASE}/api/auth/session${cacheBuster}`, {
                method: 'GET',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                },
            });
            if (response.ok) {
                const data: SessionResponse = await response.json();
                this.token = data.token;
                sessionStorage.setItem('ff_security_token', data.token);
            } else {
                console.error(`Token Manager: Handshake failed with status ${response.status}`);
            }
        } catch (error) {
            console.error('Token Manager: Failed to fetch session token', error);
        } finally {
            this.isInitializing = false;
        }

        return this.token;
    }

    async getSecurityHeader(): Promise<Record<string, string>> {
        const token = await this.getToken();
        return token ? { 'X-FileFusion-Session': token } : {};
    }
}

const tokenManagerInstance = new TokenManager();
export default tokenManagerInstance;
